import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import { C, cs } from '../styles/common';

const AMBER  = '#f59e0b';
const GREEN  = '#4ade80';
const RED    = '#f87171';
const YELLOW = '#fcd34d';

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function duration(startedAt, endedAt) {
  if (!startedAt || !endedAt) return '—';
  const ms = new Date(endedAt) - new Date(startedAt);
  const h  = Math.floor(ms / 3600000);
  const m  = Math.floor((ms % 3600000) / 60000);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function endReasonLabel(r) {
  const map = {
    target_reached:    'Target reached',
    operator_stopped:  'Operator stopped',
    material_shortage: 'Material shortage',
    machine_fault:     'Machine fault',
    admin_deleted:     'Admin deleted',
    quality_issue:     'Quality issue',
  };
  return map[r] || r || '—';
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function JobsArchive() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const now = new Date();

  const [jobs, setJobs]         = useState([]);
  const [machines, setMachines] = useState([]);
  const [moulds, setMoulds]     = useState([]);
  const [loading, setLoading]   = useState(true);

  const [month,       setMonth]       = useState(now.getMonth() + 1);
  const [year,        setYear]        = useState(now.getFullYear());
  const [machineId,   setMachineId]   = useState('');
  const [mouldId,     setMouldId]     = useState('');
  const [partSearch,  setPartSearch]  = useState('');
  const [goldenOnly,  setGoldenOnly]  = useState(false);
  const [allTime,     setAllTime]     = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/v1/machines').catch(() => ({ data: [] })),
      api.get('/api/v1/moulds').catch(() => ({ data: [] })),
    ]).then(([m, mo]) => {
      setMachines(m.data || []);
      setMoulds(mo.data || []);
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (machineId)  params.set('machine_id', machineId);
    if (mouldId)    params.set('mould_id', mouldId);
    if (goldenOnly) params.set('golden_run', 'true');
    if (!allTime) {
      params.set('month', month);
      params.set('year',  year);
    }
    api.get(`/api/v1/jobs/archive?${params}`)
      .then(r => setJobs(r.data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [month, year, machineId, mouldId, goldenOnly, allTime]);

  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = now.getFullYear(); y >= 2023; y--) years.push(y);

  const filteredJobs = partSearch
    ? jobs.filter(j => (j.parts?.name || '').toLowerCase().includes(partSearch.toLowerCase()))
    : jobs;

  const sel = {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px',
    color: C.text, padding: '5px 10px', fontSize: '12px', cursor: 'pointer',
    fontFamily: 'system-ui, sans-serif', outline: 'none',
  };

  return (
    <div style={cs.page}>
      {/* Breadcrumb */}
      <div style={cs.breadcrumb}>
        <button onClick={goBack} style={cs.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={cs.sep}>·</span>
        <button onClick={() => navigate('/admin/history?view=retired')} style={cs.backBtn}>History</button>
        <span style={cs.sep}>·</span>
        <span style={cs.crumb}>Jobs Archive</span>
      </div>

      <div style={cs.pageHeader}>
        <div>
          <h1 style={cs.pageTitle}>Jobs Archive</h1>
          <p style={cs.pageSub}>Completed and cancelled production jobs</p>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '20px',
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px' }}>

        {/* Part search */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '8px', fontSize: '11px', color: C.muted, pointerEvents: 'none' }} />
          <input
            value={partSearch}
            onChange={e => setPartSearch(e.target.value)}
            placeholder="Part name…"
            style={{ ...sel, paddingLeft: '26px', width: '140px' }}
          />
          {partSearch && (
            <button onClick={() => setPartSearch('')}
              style={{ position: 'absolute', right: '6px', background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '11px', padding: 0 }}>✕</button>
          )}
        </div>

        <div style={{ width: '1px', height: '20px', background: C.border }} />

        {/* Month / Year */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: allTime ? 0.4 : 1, pointerEvents: allTime ? 'none' : 'auto' }}>
          <i className="fa-solid fa-calendar" style={{ fontSize: '11px', color: C.muted }} />
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={sel}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div style={{ width: '1px', height: '20px', background: C.border }} />

        {/* Machine */}
        <select value={machineId} onChange={e => setMachineId(e.target.value)} style={sel}>
          <option value="">All machines</option>
          {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>

        {/* Mould */}
        <select value={mouldId} onChange={e => setMouldId(e.target.value)} style={sel}>
          <option value="">All moulds</option>
          {moulds.map(m => <option key={m.id} value={m.id}>{m.ref} — {m.name}</option>)}
        </select>

        <div style={{ width: '1px', height: '20px', background: C.border }} />

        {/* Golden only toggle */}
        <button onClick={() => setGoldenOnly(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px',
            background: goldenOnly ? '#78350f' : 'none',
            color: goldenOnly ? YELLOW : C.muted,
            border: `1px solid ${goldenOnly ? '#b45309' : C.border}`,
            borderRadius: '6px', padding: '5px 10px', fontSize: '11px',
            cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif' }}>
          <i className="fa-solid fa-star" style={{ fontSize: '10px' }} />
          Golden runs only
        </button>

        {/* All time toggle */}
        <button onClick={() => setAllTime(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px',
            background: allTime ? '#1e3a5f' : 'none',
            color: allTime ? '#93c5fd' : C.muted,
            border: `1px solid ${allTime ? '#3b82f6' : C.border}`,
            borderRadius: '6px', padding: '5px 10px', fontSize: '11px',
            cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif' }}>
          <i className="fa-solid fa-infinity" style={{ fontSize: '10px' }} />
          All time
        </button>

        <div style={{ marginLeft: 'auto', fontSize: '12px', color: C.muted }}>
          {loading ? 'Loading…' : `${filteredJobs.length} job${filteredJobs.length !== 1 ? 's' : ''}${partSearch ? ` matching "${partSearch}"` : ''}`}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={cs.center}>Loading…</div>
      ) : filteredJobs.length === 0 ? (
        <div style={{ ...cs.center, flexDirection: 'column', gap: '8px' }}>
          <i className="fa-solid fa-box-archive" style={{ fontSize: '24px', color: C.muted }} />
          <span style={{ color: C.muted, fontSize: '13px' }}>No archived jobs for this period</span>
        </div>
      ) : (
        <div style={cs.tableWrap}>
          <div style={{ ...cs.tableHeader, gridTemplateColumns: COLS, minWidth: '700px' }}>
            <span>Date</span>
            <span>Part</span>
            <span>Machine</span>
            <span>Mould</span>
            <span>Duration</span>
            <span>Output</span>
            <span>Status</span>
          </div>
          {filteredJobs.map(job => {
            const isGolden    = job.golden_run;
            const isCompleted = job.status === 'completed';
            const isCancelled = job.status === 'cancelled';
            const goodParts   = job.good_parts_count ?? 0;
            const target      = job.target_parts;
            const outputColor = !target ? C.muted : goodParts >= target ? GREEN : goodParts > 0 ? AMBER : RED;

            return (
              <div key={job.id} style={{ ...cs.tableRow, gridTemplateColumns: COLS, minWidth: '700px' }}>
                {/* Date */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: C.text }}>{fmt(job.started_at)}</div>
                  <div style={{ fontSize: '10px', color: C.muted, marginTop: '1px' }}>
                    {fmtTime(job.started_at)}{job.ended_at ? ` – ${fmtTime(job.ended_at)}` : ''}
                  </div>
                </div>

                {/* Part */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>
                      {job.parts?.name || '—'}
                    </span>
                    {isGolden && (
                      <span style={{ fontSize: '9px', background: '#78350f', color: YELLOW,
                        border: '1px solid #b45309', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                        <i className="fa-solid fa-star" style={{ marginRight: '3px' }} />Golden
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '10px', color: C.teal, fontFamily: 'monospace', marginTop: '1px' }}>
                    {job.parts?.part_number || job.material?.name || '—'}
                  </div>
                </div>

                {/* Machine */}
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {job.machines?.name || '—'}
                </div>

                {/* Mould */}
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {job.moulds?.ref ? (
                    <>
                      <span style={{ fontWeight: '600', color: C.text }}>{job.moulds.ref}</span>
                      {job.moulds.name && <span style={{ color: C.muted }}> · {job.moulds.name}</span>}
                    </>
                  ) : '—'}
                </div>

                {/* Duration */}
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {duration(job.started_at, job.ended_at)}
                </div>

                {/* Output */}
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: outputColor }}>
                    {goodParts.toLocaleString()}
                    {target ? <span style={{ fontSize: '10px', color: C.muted, fontWeight: '400' }}> / {target.toLocaleString()}</span> : ''}
                  </div>
                  {job.setter_name && (
                    <div style={{ fontSize: '10px', color: C.muted, marginTop: '1px' }}>
                      <i className="fa-solid fa-user" style={{ marginRight: '3px', fontSize: '9px' }} />
                      {job.setter_name}
                    </div>
                  )}
                </div>

                {/* Status */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                  <span style={{
                    fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '8px',
                    background: isCompleted ? '#14532d' : isCancelled ? '#450a0a' : '#1a1a2e',
                    color: isCompleted ? GREEN : isCancelled ? RED : '#93c5fd',
                    border: `1px solid ${isCompleted ? '#16a34a44' : isCancelled ? '#ef444444' : '#3b82f644'}`,
                  }}>
                    {isCompleted ? 'Completed' : isCancelled ? 'Cancelled' : job.status}
                  </span>
                  {job.end_reason && (
                    <span style={{ fontSize: '10px', color: C.muted }}>
                      {endReasonLabel(job.end_reason)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const COLS = '130px 1fr 100px 130px 80px 110px 120px';
